/**
 * La ficha de una comunidad: lo que ve cualquiera, y lo que administra quien
 * la dirige.
 *
 * Es UNA pantalla y no dos («ver» y «administrar») porque quien administra
 * pasa el 90 % del tiempo mirando lo mismo que todos y solo a veces cambia
 * algo. Con dos pantallas, cada cambio son dos clics de ida y dos de vuelta, y
 * la de administración siempre termina mostrando una versión distinta de la
 * información. Acá los controles aparecen dentro de lo que modifican.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminInvitation,
  CategoryDiscipline,
  CategoryLevel,
  CategorySex,
  Community,
  CommunityCategory,
  CommunityMember,
  CommunityMemberRole,
  PlayerSearchResult,
} from '@tennisup/contracts';
import { categoryKey, COMMUNITY_LIMITS, TOURNAMENT_LIMITS } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { ClubBlock } from '../components/ClubBlock';
import { CommunityAdmins } from '../components/CommunityAdmins';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Paginador, usePaginado } from '../components/Paginado';
import { PersonCell } from '../components/PersonCell';
import { CategoryPicker } from '../components/CategoryPicker';
import { ChallengeSettings } from '../components/ChallengeSettings';
import { ClubReports, ReportPlayerBox } from '../components/PlayerReports';
import { WhatsAppIcon } from '../components/icons';
import {
  AutoApproveToggle,
  JoinBox,
  JoinRequests,
  LeaveBox,
  MyCategoryBox,
  PointRequests,
  RankingRequests,
} from '../components/JoinSection';
import { LogoBox } from '../components/LogoBox';
import { MatchSettings } from '../components/MatchSettings';
import { Select } from '../components/Select';
import { PlayedUpNotices, RankingSection } from '../components/RankingSection';
import { useRemembered } from '../hooks/useRemembered';
import { usePlayerSearch, MIN_SEARCH_LENGTH } from '../hooks/usePlayerSearch';
import { foldForSearch } from '../components/CountryPicker';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { categoryName, clubLevels, rankingName } from '../lib/categoryName';

/**
 * El valor de «todas las categorías» en el filtro.
 *
 * Una palabra y no la cadena vacía: es el valor de una opción y termina en un
 * `id` del HTML. `categoryKey` arma «NIVEL-SEXO», así que nunca choca.
 */
const TODAS = 'TODAS';

export function CommunityPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id = '' } = useParams();
  const [community, setCommunity] = useState<Community | null>(null);
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * ¿Se ven los jugadores?
   *
   * UN SOLO INTERRUPTOR PARA LAS DOS LISTAS —el ranking y la de agregar—
   * porque son la misma pregunta: «¿quiero la nómina en pantalla o no?». Dos
   * interruptores separados obligarían a apagar dos cosas para conseguir una.
   *
   * Se recuerda POR CLUB: quien administra uno de mil jugadores lo quiere
   * cerrado siempre, y en el club chico de al lado lo quiere abierto. Un solo
   * valor para todos haría que abrir un club cambiara cómo se ve el otro.
   *
   * Es preferencia de pantalla, no privacidad: esto no esconde a nadie de
   * nadie, solo deja de dibujar una lista larga. Si algún día hace falta que
   * la nómina sea privada de verdad, eso se decide en el servidor.
   */
  const [verJugadores, setVerJugadores] = useRemembered(`club:${id}:jugadores`, true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [ficha, gente] = await Promise.all([api.findCommunity(id), api.listMembers(id)]);
      setCommunity(ficha);
      setMembers(gente);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <AppLayout>
        <Alert kind="error">{error}</Alert>
        <div className="links">
          <Link to="/clubes">{t('newCommunity.backToList')}</Link>
        </div>
      </AppLayout>
    );
  }

  if (community === null) {
    return (
      <AppLayout>
        <p className="help">{t('common.loading')}</p>
      </AppLayout>
    );
  }

  const puedeAdministrar = community.myRole === 'ADMIN';
  /**
   * MIEMBRO ACTIVO, que no es lo mismo que tener un rol: una solicitud
   * pendiente todavía no está adentro, y ofrecerle reportar a alguien sería un
   * botón que el servidor rechaza.
   */
  const soyMiembro = community.myMembershipStatus === 'ACTIVE';
  const logo = assetUrl(community.logoPath);

  return (
    <AppLayout>
      {/* A la lista de clubes. `navigate(-1)` devuelve por donde se vino
          —desde el ranking, desde una invitación—; la lista es el respaldo
          para quien abrió el enlace directo desde WhatsApp. */}
      <BackButton to="/clubes" />
      <div className="community-header">
        {logo !== null && <img className="community-logo big" src={logo} alt="" />}
        <div>
          <h1>{community.name}</h1>
          <p className="community-location">{community.locationText}</p>
          <p className="community-tags">
            {community.status !== 'APPROVED' && (
              <span className={`tag tag-${community.status.toLowerCase()}`}>
                {t(`communityStatus.${community.status}`)}
              </span>
            )}
            <span className="tag">{t('communities.memberCount', { count: community.memberCount })}</span>
          </p>
        </div>
      </div>

      <p>{community.description}</p>

      {community.whatsappGroupUrl !== null && (
        // EL ÍCONO, igual que en la tarjeta: el renglón de texto azul
        // competía con el nombre del club por la atención y no decía nada
        // que el ícono no diga. El nombre viaja adentro del svg, así que un
        // lector de pantalla sigue diciendo qué es.
        <p>
          <a
            className="community-whatsapp"
            href={community.whatsappGroupUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={t('communities.openGroup')}
          >
            <WhatsAppIcon title={t('communities.openGroup')} />
          </a>
        </p>
      )}

      {community.reviewNote !== null && (
        <Alert kind="info">
          <strong>{t('communities.reviewNote')}:</strong> {community.reviewNote}
        </Alert>
      )}

      {/* LO QUE SE AVISA ES LA FALTA DEL PERMISO, NO SU PRESENCIA.
          La etiqueta verde «Puede crear torneos» decía lo normal y no servía
          de nada. Esto dice lo raro: por qué no aparece el botón de crear
          torneo. Solo a quien administra, que es quien puede hacer algo. */}
      {puedeAdministrar && !community.canCreateTournaments && (
        <Alert kind="info">{t('communities.cannotRunTournamentsYet')}</Alert>
      )}

      {/* QUIÉN ADMINISTRA, arriba de todo lo demás: es a quién escribirle si
          algo de lo que sigue no cuadra. Ver `CommunityAdmins`. */}
      <CommunityAdmins communityId={community.id} />

      {/*
        DE ACÁ PARA ABAJO, LA PANTALLA VA EN CUADROS.

        Javier: «En la vista del club quiero que separe las vistas en cuadros
        para poder entenderse mejor [...] pero sí quiero poder ver las
        diferencias tanto para el administrador del club como para el
        jugador».

        Eran once secciones seguidas, todas con el mismo peso y las de quien
        administra intercaladas con las de cualquiera. El orden de adentro NO
        cambió —cada sección sigue donde estaba, por las razones que ya estaban
        escritas— y lo que cambia es que ahora se ven los grupos.

        LOS CUADROS QUE SON SOLO DE QUIEN ADMINISTRA LO DICEN, con una etiqueta
        de texto. Es lo que contesta «¿esto lo ven los jugadores?» sin tener
        que entrar con otra cuenta a averiguarlo.
      */}

      {/* PRIMERO LO QUE ESPERA RESPUESTA: es lo único de la pantalla que le
          pide una decisión a quien la mira. Enterrado debajo de la lista de
          jugadores es cómo alguien espera tres semanas por una respuesta.

          EL CUADRO NO EXISTE PARA QUIEN NO ADMINISTRA. Y para quien sí, cada
          sección de adentro se calla sola cuando su cola está vacía. */}
      {puedeAdministrar && (
        <ClubBlock
          title={t('communities.blockDecisions')}
          help={t('communities.blockDecisionsHelp')}
          adminOnly
        >
          <JoinRequests community={community} onChanged={() => void load()} />
          {/* LAS TRES COLAS SEPARADAS, cada una con SUS botones.
              Eran una sola sección con tres clases de pedido adentro, y los
              botones eran los del ingreso para las tres: rechazar un pedido de
              categoría llamaba al `DELETE` de la membresía y sacaba del club a
              un socio. Lo que las separa no es qué muestran —muestran lo
              mismo— sino qué ruta las contesta. */}
          <RankingRequests community={community} onChanged={() => void load()} />
          <PointRequests community={community} onChanged={() => void load()} />
          {/* LOS REPORTES, acá por la misma razón: piden una decisión. Se
              dibujan solo si hay alguno. */}
          <ClubReports communityId={community.id} />
          {/* El aviso de quien juega arriba también es una decisión, pero
              puede esperar: va último dentro del cuadro. */}
          <PlayedUpNotices communityId={community.id} />
        </ClubBlock>
      )}

      {/* MI LUGAR EN EL CLUB: entrar, mi categoría, salir.
          Es lo que cada quien puede hacer POR SÍ MISMO, y por eso va aparte de
          todo lo que se configura. Las tres cajas ya se callan solas cuando no
          aplican —quien no es socio no ve «salir»—, así que el cuadro no lleva
          condición propia. */}
      <ClubBlock title={t('communities.blockMe')}>
        <JoinBox community={community} onJoined={setCommunity} />
        <MyCategoryBox community={community} onChanged={setCommunity} />
        {/* La puerta de salida, al lado de la de entrada: es la misma pregunta
            —«¿qué tengo que ver con este club?»— contestada al revés. Va
            DESPUÉS de la categoría porque es lo último que alguien busca acá,
            y ponerla arriba la ofrecería. */}
        <LeaveBox community={community} onLeft={() => void load()} />
      </ClubBlock>

      {/* LOS TORNEOS, ARRIBA DE TODO LO QUE SE CONFIGURA.
          Estaba al final de la página, después de todo lo que un club decide
          una vez; se subió porque es a lo que la gente entra. SIN CUADRO: es
          un botón, y una caja alrededor de un botón es una caja alrededor de
          nada. */}
      <p className="links">
        <Link className="button-like" to={`/clubes/${community.id}/torneos`}>
          {t('tournaments.title')}
        </Link>
      </p>

      {/* JUGADORES Y CATEGORÍAS. Lo ve cualquiera, y tiene que ser así: el
          ranking y las categorías son las reglas con las que se juega, y quien
          está por retar a alguien necesita poder mirarlas.

          EL INTERRUPTOR DE VER JUGADORES VA ARRIBA DE LAS DOS SECCIONES QUE
          APAGA, y no dentro de una de ellas: metido adentro del ranking
          parecería que solo apaga el ranking, y con todo cerrado no habría
          dónde volver a prenderlo. */}
      <ClubBlock title={t('communities.blockPlayers')}>
        <p className="links">
          <button type="button" className="link" onClick={() => setVerJugadores(!verJugadores)}>
            {verJugadores ? t('members.hidePlayers') : t('members.showPlayers')}
          </button>
        </p>

        {verJugadores && <RankingSection community={community} />}

        <CategoriesSection
          community={community}
          members={members}
          canManage={puedeAdministrar}
          onChanged={setCommunity}
        />

        {puedeAdministrar && (
          <AdminsSection community={community} members={members} onChanged={() => void load()} />
        )}

        {verJugadores && (
          <MembersSection
            community={community}
            members={members}
            canManage={puedeAdministrar}
            onChanged={() => void load()}
          />
        )}
      </ClubBlock>

      {/* LOS INTERRUPTORES DEL CLUB, TODOS JUNTOS.
          Los tres son de la misma naturaleza —reglas que quien administra fija
          una vez— así que van seguidos: repartidos por la página, cada uno
          parecía de la sección que tuviera encima.

          LOS DE RETOS VAN DESPUÉS DE LOS DE PARTIDO y no mezclados: los de
          arriba son sobre un partido que ya existe —quién lo anota, quién se
          escribe— y estos son sobre si el partido puede existir. Mezclados,
          quien busca «apagar los retos» lo encontraría entre casillas que
          hablan de WhatsApp. */}
      {puedeAdministrar && (
        <ClubBlock
          title={t('communities.blockSettings')}
          help={t('communities.blockSettingsHelp')}
          adminOnly
        >
          <AutoApproveToggle community={community} onChanged={setCommunity} />
          <MatchSettings community={community} onChanged={setCommunity} />
          <ChallengeSettings community={community} onChanged={setCommunity} />
        </ClubBlock>
      )}

      {/* DATOS Y REGLAMENTO. Los LEE cualquiera y los EDITA quien administra
          —cada sección ya distingue con `canManage`—, así que este cuadro NO
          lleva la etiqueta de «solo quien administra»: la llevaría mintiendo.

          ACÁ ESTABAN TAMBIÉN LAS CUENTAS DEL CLUB, y ya no. Javier: «El torneo
          va a tener reglamento y cuentas, el club solo va a tener reglamento».
          La memoria de las cuentas pasó a ser el torneo anterior; ver
          `findLatestOfCommunity` en el servidor. */}
      <ClubBlock title={t('communities.blockAbout')}>
        <DetailsSection community={community} canManage={puedeAdministrar} onChanged={setCommunity} />
        <RulesSection community={community} canManage={puedeAdministrar} onChanged={setCommunity} />
      </ClubBlock>

      {/* REPORTAR VA AL FINAL Y PLEGADO. Es de las cosas que se usan tres
          veces al año, y un formulario siempre abierto le daría a un problema
          excepcional el mismo peso visual que a la lista de jugadores. Lo ve
          cualquier miembro: quien ve el problema casi nunca es quien manda.
          SIN CUADRO: ya se dibuja plegado, que es su propia caja. */}
      {soyMiembro && (
        <ReportPlayerBox
          communityId={community.id}
          members={members}
          meId={session?.user.id ?? ''}
        />
      )}

    </AppLayout>
  );
}

// =====================================================================
// Categorías
// =====================================================================

function CategoriesSection({
  community,
  members,
  canManage,
  onChanged,
}: {
  community: Community;
  members: CommunityMember[];
  canManage: boolean;
  onChanged: (community: Community) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CommunityCategory[]>(community.categories);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Las categorías que hoy tienen gente adentro no se pueden desmarcar.
   *
   * Se calcula acá con los miembros que la pantalla ya tiene, en vez de pedirle
   * la lista al servidor: es el mismo dato. Y el servidor lo comprueba igual
   * —esto es para que la casilla se vea trabada y no para confiar en ella.
   */
  const bloqueadas = new Set(
    members
      .filter((member): member is typeof member & { sex: NonNullable<typeof member.sex> } => member.sex !== null)
      // La categoría de un socio es la de SINGLES: es su ranking de siempre.
      .map((member) => categoryKey({ ...member, discipline: 'SINGLES' })),
  );

  async function save(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(await api.setCommunityCategories(community.id, draft));
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('categories.title')}</h2>
        {canManage && !editing && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setDraft(community.categories);
              setError(null);
              setEditing(true);
            }}
          >
            {t('common.edit')}
          </button>
        )}
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {editing ? (
        <>
          <CategoryPicker value={draft} onChange={setDraft} disabled={sending} lockedKeys={bloqueadas} />
          <div className="button-row">
            <button type="button" onClick={() => void save()} disabled={sending}>
              {sending ? t('common.sending') : t('common.save')}
            </button>
            <button type="button" className="link" onClick={() => setEditing(false)} disabled={sending}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : community.categories.length === 0 ? (
        <p className="help">{t('categories.empty')}</p>
      ) : (
        <p className="community-tags">
          {community.categories.map((category) => (
            <span className="tag tag-category" key={categoryKey(category)}>
              {categoryName(t, category)}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

// =====================================================================
// Quién administra el club
// =====================================================================

/**
 * DAR Y QUITAR EL PERMISO DE ADMINISTRAR, en su propio cuadro.
 *
 * ESTABA EN CADA FILA DE LA LISTA DE JUGADORES Y ESO ESTABA MAL. Repartir el
 * control del club no es una acción que uno haga de pasada mientras recorre
 * doscientos nombres: es una decisión, y un botón que aparece doscientas
 * veces al lado de «Quitar» es un botón que tarde o temprano se toca por
 * error. Acá se busca a la persona a propósito, se ve quién administra hoy,
 * y recién ahí se le da.
 *
 * LO VE CUALQUIERA QUE ADMINISTRE, que es lo mismo que comprueba el
 * servidor. Antes era solo del dueño y el miedo estaba al revés: los clubes
 * los abre una persona y los mueven tres, y bastaba que quien lo creó se
 * fuera de viaje para que nadie pudiera sumar a quien hacía falta.
 *
 * A QUIEN CREÓ EL CLUB NO SE LE PUEDE QUITAR, y por eso aparece en la lista
 * pero sin botón. Es lo que hace que dar y quitar puedan ser la misma regla:
 * pase lo que pase entre los administradores, siempre queda alguien que
 * puede deshacerlo. ESCONDERLO SERÍA PEOR que mostrarlo sin botón: un club
 * que dice «nadie administra» cuando lo administra quien lo creó es una
 * pantalla que miente.
 *
 * Y TAMPOCO SOBRE UNO MISMO: quitarse el permiso desde el cuadro donde uno
 * lo reparte es la clase de toque que se lamenta enseguida, y para eso hace
 * falta otra persona que lo devuelva.
 */
function AdminsSection({
  community,
  members,
  onChanged,
}: {
  community: Community;
  members: CommunityMember[];
  onChanged: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendientes, setPendientes] = useState<AdminInvitation[]>([]);
  const [recienInvitado, setRecienInvitado] = useState(false);

  const yo = session?.user.id ?? '';
  const dueño = community.createdBy.id;

  /*
   * LAS QUE ESPERAN RESPUESTA, junto a quienes ya administran.
   *
   * ES LO QUE EVITA QUE SE INVITE DOS VECES: sin verlas, una invitación es un
   * correo que se fue y no dejó rastro, y quien administra vuelve a apretar
   * el botón porque no pasó nada visible en la pantalla.
   */
  useEffect(() => {
    let vigente = true;
    api
      .listAdminInvitations(community.id)
      .then((filas) => {
        if (vigente) setPendientes(Array.isArray(filas) ? filas : []);
      })
      .catch(() => {
        // Que no se puedan listar no puede romper el cuadro de quienes
        // administran, que es lo que la persona vino a ver.
      });
    return () => {
      vigente = false;
    };
  }, [community.id, recienInvitado]);

  /**
   * ESTE CUADRO ES DE PERSONAS, NO DE TABLAS, y por eso se deduplica.
   *
   * `members` trae una fila POR RANKING desde el paso 4: quien compite en dos
   * categorías del mismo club viene dos veces, con el mismo `userId`. La
   * lista de jugadores quiere esas dos filas —son dos rankings distintos—,
   * pero repartir el permiso de administrar no tiene nada que ver con en
   * cuántas tablas juega alguien: sin esto, quien está en dos aparecería dos
   * veces acá, con dos botones de «quitar» para un solo permiso.
   */
  function unaPorPersona(filas: CommunityMember[]): CommunityMember[] {
    const vistos = new Set<string>();
    return filas.filter((fila) => {
      if (vistos.has(fila.userId)) return false;
      vistos.add(fila.userId);
      return true;
    });
  }

  /** Quiénes administran hoy, incluido quien creó el club. Una fila por persona. */
  const administran = unaPorPersona(
    members.filter((member) => member.role === 'ADMIN' || member.userId === dueño),
  );
  /**
   * A quiénes se les puede DAR el permiso: a todos menos a quien creó el club.
   *
   * A uno mismo no hace falta excluirlo acá: la búsqueda de abajo solo lista
   * a quien NO administra, y quien está mirando este cuadro administra. Se
   * probó agregar la exclusión y no hubo forma de escribir una prueba que
   * fallara al sacarla — código que no se puede probar es código que nadie
   * sabe si funciona.
   */
  const tocables = unaPorPersona(members.filter((member) => member.userId !== dueño));

  /**
   * La búsqueda arranca VACÍA y no lista a nadie.
   *
   * Con doscientos jugadores, mostrarlos todos convierte este cuadro en la
   * lista de jugadores otra vez — que es justo de lo que se lo sacó. Se
   * escribe un nombre y aparece quien calce.
   */
  const buscado = foldForSearch(query.trim());
  const encontrados =
    buscado === ''
      ? []
      : tocables
          .filter(
            (member) =>
              member.role !== 'ADMIN' &&
              // A quien ya tiene una invitación esperando no se le ofrece de
              // nuevo: el servidor lo rechazaría y el botón sería una trampa.
              !pendientes.some((invitacion) => invitacion.userId === member.userId) &&
              (foldForSearch(member.displayName).includes(buscado) ||
                foldForSearch(member.fullName).includes(buscado)),
          )
          .slice(0, 8);

  /** QUITAR el permiso. Darlo pasa por `invitar`. */
  async function cambiarRol(member: CommunityMember, role: CommunityMemberRole): Promise<void> {
    setBusyId(member.userId);
    setError(null);
    try {
      await api.setMemberRole(community.id, member.userId, role);
      setQuery('');
      onChanged();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * INVITA. No da el permiso: manda el correo con el enlace.
   *
   * Javier: «prefiero que lo confirmen por correo». Administra cuando acepte.
   */
  async function invitar(member: CommunityMember): Promise<void> {
    setBusyId(member.userId);
    setError(null);
    try {
      await api.inviteAdmin(community.id, member.userId);
      setQuery('');
      setRecienInvitado((antes) => !antes);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  /** Cancela una que se mandó por error: su enlace deja de servir. */
  async function cancelar(invitacion: AdminInvitation): Promise<void> {
    setBusyId(invitacion.userId);
    setError(null);
    try {
      await api.revokeAdminInvitation(community.id, invitacion.id);
      setRecienInvitado((antes) => !antes);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <h2>{t('members.adminsTitle')}</h2>
      <p className="help">{t('members.roleHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {administran.length === 0 ? (
        <p className="help">{t('members.noAdmins')}</p>
      ) : (
        <div className="table-scroll">
          <table className="people-table">
            <thead>
              <tr>
                <th scope="col">{t('player.player')}</th>
                <th scope="col">
                  <span className="visually-hidden">{t('player.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {administran.map((member) => (
                <tr key={member.userId}>
                  <PersonCell
                    userId={member.userId}
                    displayName={member.displayName}
                    avatarPath={member.avatarPath}
                    note={member.fullName}
                  />
                  <td className="actions">
                    {member.userId === dueño ? (
                      <span className="tag">{t('members.owner')}</span>
                    ) : member.userId === yo ? (
                      // Quitarse el permiso desde el cuadro donde uno lo
                      // reparte es la clase de toque que se lamenta enseguida.
                      <span className="tag">{t('members.you')}</span>
                    ) : (
                    <button
                      type="button"
                      className="link"
                      disabled={busyId === member.userId}
                      onClick={() => void cambiarRol(member, 'MEMBER')}
                    >
                      {t('members.removeAdmin')}
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendientes.length > 0 && (
        <>
          <h3>{t('adminInvite.pendingTitle')}</h3>
          <p className="help">{t('adminInvite.sent')}</p>
          <div className="table-scroll">
            <table className="people-table">
              <thead>
                <tr>
                  <th scope="col">{t('player.player')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('player.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((invitacion) => (
                  <tr key={invitacion.id}>
                    <PersonCell
                      userId={invitacion.userId}
                      displayName={invitacion.displayName}
                      avatarPath={invitacion.avatarPath}
                      note={t('adminInvite.pending')}
                    />
                    <td className="actions">
                      <button
                        type="button"
                        className="link"
                        disabled={busyId === invitacion.userId}
                        onClick={() => void cancelar(invitacion)}
                      >
                        {t('adminInvite.revoke')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="field">
        <label htmlFor="admin-search">{t('members.searchToPromote')}</label>
        <input
          id="admin-search"
          type="search"
          autoComplete="off"
          placeholder={t('members.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {buscado !== '' && encontrados.length === 0 ? (
        <p className="help">{t('members.noneFound')}</p>
      ) : (
        encontrados.length > 0 && (
          <div className="table-scroll">
            <table className="people-table">
              <thead>
                <tr>
                  <th scope="col">{t('player.player')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('player.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {encontrados.map((member) => (
                  <tr key={member.userId}>
                    <PersonCell
                      userId={member.userId}
                      displayName={member.displayName}
                      avatarPath={member.avatarPath}
                      note={member.fullName}
                    />
                    <td className="actions">
                      <button
                        type="button"
                        disabled={busyId === member.userId}
                        onClick={() => void invitar(member)}
                      >
                        {t('members.makeAdmin')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

// =====================================================================
// Miembros
// =====================================================================

function MembersSection({
  community,
  members,
  canManage,
  onChanged,
}: {
  community: Community;
  members: CommunityMember[];
  canManage: boolean;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>(TODAS);
  const [query, setQuery] = useState('');
  /**
   * LOS PUNTOS, POR PERSONA **Y POR TABLA**.
   *
   * La llave es `userId/categoryKey(...)` y no `userId` a secas: desde que se
   * puede competir en varios rankings del mismo club, la lista trae a esa
   * persona una vez por tabla y cada fila tiene SU puntaje. Con la llave
   * vieja, las dos filas mostraban el mismo número —el que la respuesta
   * hubiera dejado de último— y el orden de la tabla salía de un dato que no
   * es de esa fila.
   */
  const [points, setPoints] = useState<Map<string, number>>(new Map());

  /**
   * LA IDENTIDAD DE UNA FILA ES LA TABLA, NO LA PERSONA.
   *
   * Es la llave con la que se juntan los puntos, y por la misma razón: desde que se
   * puede competir en dos rankings del mismo club, esta lista trae a alguien
   * UNA VEZ POR TABLA. `userId` solo ya no identifica una fila.
   *
   * ESTO ESTABA MAL EN TRES LUGARES A LA VEZ y los tres se veían como cosas
   * distintas. Con `key={member.userId}` React tiene dos hijos con la misma
   * llave: deja de poder emparejar cada fila con la de la vuelta anterior, y
   * entonces (a) duplica las que no supo casar —Vero salía cinco veces
   * teniendo dos inscripciones—, y (b) al filtrar deja pegadas las viejas
   * —buscar «VERO» mostraba tres «Zurdo tornado»—. Y con `busyId` por
   * persona, (c) los botones de las dos filas se bloqueaban juntos.
   *
   * Javier lo vio como datos repetidos y pidió el SQL para borrarlos. No
   * había nada que borrar: la base tenía dos filas, y su llave primaria
   * `(communityId, userId, level, sex, discipline)` hace imposible que
   * hubiera más. Todo era de esta pantalla.
   */
  function llaveDeFila(fila: {
    userId: string;
    level: CategoryLevel;
    sex: CategorySex;
    discipline: CategoryDiscipline;
  }): string {
    return `${fila.userId}/${categoryKey(fila)}`;
  }

  const [error, setError] = useState<string | null>(null);
  /** Cuál FILA está esperando respuesta, no cuál persona. Ver `llaveDeFila`. */
  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Los puntos se piden aparte y se juntan acá.
   *
   * Calcularlos necesita los torneos, sus inscritos y sus partidos; el
   * listado de jugadores vive en otro lado. Copiar esa cuenta daría dos
   * rankings que tarde o temprano dicen números distintos. Juntarlos es
   * trabajo de pantalla, y si la consulta falla la lista igual se ve: sin
   * puntos, pero se ve.
   */
  useEffect(() => {
    let vivo = true;
    api
      .communityPoints(community.id)
      .then((filas) => {
        if (vivo) setPoints(new Map(filas.map((f) => [llaveDeFila(f), f.points])));
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [community.id]);

  const buscado = query.trim().toLowerCase();
  const filtrados = members
    .filter(
      (m) =>
        filter === TODAS ||
        // LA TERNA DE LA FILA, entera. El `m.sex !== null` que había acá ya
        // no puede pasar —una inscripción dice siempre en qué tabla está— y
        // la disciplina sale de la fila en vez de estar escrita 'SINGLES': el
        // día que haya rankings de dobles, este filtro ya funciona.
        categoryKey(m) === filter,
    )
    .filter((m) => {
      if (buscado === '') return true;
      // Se busca por apodo Y por nombre legal: quien administra conoce a
      // algunos por uno y a otros por el otro. El nombre legal solo lo tiene
      // quien administra, así que para el resto la búsqueda es por apodo.
      const donde = `${m.displayName} ${canManage ? m.fullName : ''}`.toLowerCase();
      return donde.includes(buscado);
    })
    // DE MÁS PUNTOS A MENOS, que es el orden que pidió Javier. A igualdad,
    // por apodo: sin ese desempate, dos personas con los mismos puntos
    // cambiarían de lugar entre una carga y otra.
    .sort(
      (a, b) =>
        (points.get(llaveDeFila(b)) ?? 0) - (points.get(llaveDeFila(a)) ?? 0) ||
        a.displayName.localeCompare(b.displayName, 'es'),
    );

  /**
   * DE A DIEZ. Un club con ochenta socios eran ochenta filas —catorce
   * pantallas de teléfono— y ochenta fotos descargándose antes de poder leer
   * la primera.
   *
   * `reiniciarCon` junta lo que cambia la lista entera: filtrar por categoría
   * o escribir en el buscador. Quedarse en la página 3 de OTRA lista es el
   * defecto que hace pensar que la búsqueda no encontró nada.
   */
  const pagina = usePaginado(filtrados, `${filter}|${buscado}`);

  async function cambiarCategoria(member: CommunityMember, level: CategoryLevel): Promise<void> {
    setBusyId(llaveDeFila(member));
    setError(null);
    try {
      await api.updateMember(community.id, member.userId, { level });
      onChanged();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * RESPONDER UN PEDIDO DE CAMBIO DE CATEGORÍA.
   *
   * Aprobar mueve la categoría y borra el pedido en una sola escritura del
   * lado del servidor; acá solo se dice sí o no.
   */
  async function responderCategoria(member: CommunityMember, aprobar: boolean): Promise<void> {
    setBusyId(llaveDeFila(member));
    setError(null);
    try {
      await api.reviewCategoryChange(community.id, member.userId, aprobar);
      onChanged();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  async function quitar(member: CommunityMember): Promise<void> {
    setBusyId(llaveDeFila(member));
    setError(null);
    try {
      await api.removeMember(community.id, member.userId);
      onChanged();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('members.title')}</h2>
      </div>
      <p className="help">{t('members.help')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="member-search">{t('members.searchInClub')}</label>
        <input
          id="member-search"
          type="search"
          value={query}
          autoComplete="off"
          placeholder={t('members.searchInClubPlaceholder')}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {community.categories.length > 0 && (
        <div className="field">
          <label htmlFor="member-filter">{t('members.filter')}</label>
          <Select
            id="member-filter"
            value={filter}
            options={[
              { value: TODAS, label: t('members.allCategories') },
              ...community.categories.map((category) => ({
                value: categoryKey(category),
                label: categoryName(t, category),
              })),
            ]}
            onChange={setFilter}
          />
        </div>
      )}

      {filtrados.length === 0 ? (
        // Se distingue «el club no tiene jugadores» de «tu búsqueda no
        // encontró nada»: son dos situaciones distintas y la segunda tiene
        // arreglo borrando lo que escribiste.
        <p className="help">
          {members.length === 0 ? t('members.empty') : t('members.noneFound')}
        </p>
      ) : (
        <div className="table-scroll">
        <table className="people-table">
          <thead>
            <tr>
              <th scope="col">{t('player.player')}</th>
              <th scope="col" className="num">{t('ranking.points')}</th>
              <th scope="col">{t('ranking.category')}</th>
              {canManage && <th scope="col"><span className="visually-hidden">{t('player.actions')}</span></th>}
            </tr>
          </thead>
          <tbody>
          {pagina.visibles.map((member) => (
            <tr key={llaveDeFila(member)}>
              <PersonCell
                userId={member.userId}
                displayName={member.displayName}
                avatarPath={member.avatarPath}
                {...(canManage ? { note: member.fullName } : {})}
              />

              <td className="num">{points.get(llaveDeFila(member)) ?? 0}</td>

              <td>
              {canManage ? (
                /*
                 * EL DESPLEGABLE DICE LA TABLA ENTERA: «C · Femenino».
                 *
                 * Solo decía el nivel, y con dos filas de la misma persona —C
                 * femenino y C masculino— las dos decían «C»: indistinguibles,
                 * o sea que se leían como una fila repetida. Esa fue la lectura
                 * que hizo pensar que la base tenía basura.
                 *
                 * EL SEXO VA ADENTRO Y NO EN UNA ETIQUETA AL LADO. Primero lo
                 * puse al lado y quedaba flotando sobre la fila: dos cosas
                 * diciendo la categoría en la misma columna. Adentro es lo
                 * correcto —este control ES la categoría de la fila—, aunque el
                 * sexo no se cambie desde acá: todas las opciones son de la
                 * misma tabla, así que decirlo en cada una no promete nada
                 * falso y sí deja claro cuál de las dos filas se está tocando.
                 *
                 * LOS NIVELES SALEN DE `clubLevels`, QUE LOS DEDUPLICA. Un club
                 * que corre «A femenino singles» y «A femenino dobles» tiene dos
                 * categorías y UN nivel A; con `community.categories` crudo la
                 * lista salía «Open, A+, A, A, B, B» —Javier la vio así— y las
                 * dos «A» tenían el mismo `value`, así que no se entendía cuál
                 * quedaba marcada. `clubLevels` ya existía y resolvía esto; este
                 * desplegable no lo estaba usando.
                 */
                <Select
                  id={`member-level-${llaveDeFila(member)}`}
                  aria-label={t('members.categoryOf', { name: member.displayName })}
                  value={member.level}
                  disabled={busyId === llaveDeFila(member)}
                  options={(member.sex === 'MIXED'
                    ? /* MIXTO NO ES UNA TABLA DE RANKING —los rankings son de
                         singles— así que no hay niveles que ofrecer. El tipo de
                         `member.sex` lo permite porque la MISMA forma describe
                         las categorías de torneo, y ahí sí existe. Se contesta
                         con la lista vacía en vez de forzar el tipo: un `as` acá
                         escondería justo el caso que el compilador señaló. */
                      []
                    : clubLevels(community.categories, member.sex)
                  ).map((level) => ({
                    value: level,
                    label: rankingName(t, { level, sex: member.sex }),
                  }))}
                  onChange={(level) => void cambiarCategoria(member, level)}
                />
              ) : (
                /* LA TABLA EN LA QUE COMPITE, con `categoryName`.
                   Acá el nombre del cuadro se armaba a mano, interpolando el
                   sexo del miembro dentro de la llave de traducción —justo lo
                   que la prueba de `categoryName` prohíbe, y escrito así se
                   le escapaba—. Y ahora además sería falso: el sexo de esta
                   fila es el de la TABLA, no el de la persona, y una mujer
                   inscrita en la masculina saldría etiquetada «Masculino». */
                <span className="tag tag-category">{categoryName(t, member)}</span>
              )}

              {/*
                EL PEDIDO DE CATEGORÍA, JUSTO AL LADO DE LA CATEGORÍA.

                Es donde se está mirando cuando la pregunta aparece —«¿esta
                persona es B?»— y las dos respuestas están a un toque. En una
                sección aparte, arriba o abajo, hay que acordarse de ir a
                verla; acá no hay nada que recordar.

                Lo ve solo quien administra: es una decisión del club.
              */}
              {canManage && member.requestedLevel !== null && (
                <span className="category-request">
                  <span className="help">
                    {t('members.wantsCategory', {
                      level: t(`categoryLevel.${member.requestedLevel}`),
                    })}
                  </span>
                  <button
                    type="button"
                    className="link"
                    disabled={busyId === llaveDeFila(member)}
                    onClick={() => void responderCategoria(member, true)}
                  >
                    {t('members.approveCategory')}
                  </button>
                  <button
                    type="button"
                    className="link"
                    disabled={busyId === llaveDeFila(member)}
                    onClick={() => void responderCategoria(member, false)}
                  >
                    {t('members.rejectCategory')}
                  </button>
                </span>
              )}

              {/* La etiqueta la ve CUALQUIERA: saber a quién escribirle
                  cuando hay un problema con el club no es información
                  reservada. Repartir el permiso, en cambio, tiene su propio
                  cuadro más abajo — ver `AdminsSection`. */}
              {member.role === 'ADMIN' && <span className="tag">{t('members.isAdmin')}</span>}
              </td>

              {canManage && (
                <td className="actions">
                <button
                  type="button"
                  className="link"
                  disabled={busyId === llaveDeFila(member)}
                  onClick={() => void quitar(member)}
                >
                  {t('members.remove')}
                </button>
                </td>
              )}
            </tr>
          ))}
          </tbody>
        </table>
        </div>
      )}
      {/* DEBAJO DE LA TABLA Y FUERA DE SU CAJA: la caja se desplaza de lado
          cuando la tabla no entra, y los botones de página no tienen por qué
          irse de la pantalla con ella. */}
      <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />

      {canManage && <AddMemberSection community={community} onAdded={onChanged} />}
    </section>
  );
}

function AddMemberSection({
  community,
  onAdded,
}: {
  community: Community;
  onAdded: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [levels, setLevels] = useState<Record<string, CategoryLevel>>({});
  const [agregados, setAgregados] = useState<ReadonlySet<string>>(new Set());

  // La búsqueda sale sola a partir de la tercera letra. Todo lo delicado —el
  // retardo, cancelar la anterior, no pisar una respuesta con otra que llegó
  // tarde— vive en el hook.
  const busqueda = usePlayerSearch(community.id, query);
  const results = busqueda.results;

  /**
   * `level` se recibe y no se lee del estado.
   *
   * El estado solo tiene lo que la persona CAMBIÓ; el select arranca
   * mostrando la primera categoría disponible sin escribir nada. Leyéndolo
   * del estado, agregar a alguien sin tocar el selector no hacía nada: el
   * botón respondía y no pasaba nada, que es la peor forma de fallar.
   */
  async function add(player: PlayerSearchResult, level: CategoryLevel): Promise<void> {
    setError(null);
    try {
      await api.addMember(community.id, { userId: player.userId, level });
      // Se marca como agregado en vez de recargar la búsqueda: quien está
      // agregando a cinco personas no quiere que la lista se le mueva debajo
      // del dedo después de cada una.
      setAgregados((current) => new Set(current).add(player.userId));
      onAdded();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <div className="add-member">
      <h3>{t('members.addTitle')}</h3>
      <p className="help">{t('members.addHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="player-search">{t('members.searchLabel')}</label>
        <input
          id="player-search"
          type="search"
          value={query}
          autoComplete="off"
          placeholder={t('members.searchPlaceholder')}
          onChange={(e) => setQuery(e.target.value)}
          // La lista de abajo aparece y cambia sola: sin esto, un lector de
          // pantalla no se entera de que llegaron resultados.
          aria-describedby="player-search-status"
        />
      </div>

      <p className="help" id="player-search-status" role="status">
        {busqueda.tooShort
          ? t('members.keepTyping', { min: MIN_SEARCH_LENGTH })
          : busqueda.searching
            ? t('members.searching')
            : query.trim().length < MIN_SEARCH_LENGTH
              ? ''
              : results.length === 0
                ? t('members.none')
                : t('members.found', { n: results.length })}
      </p>

      {busqueda.error !== null && <Alert kind="error">{errorMessage(t, busqueda.error)}</Alert>}

      {results.length === 0 ? null : (
        <div className="table-scroll">
        <table className="people-table">
          <thead>
            <tr>
              <th scope="col">{t('player.player')}</th>
              <th scope="col">
                <span className="visually-hidden">{t('player.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
          {results.map((player) => {
            // Sin sexo en el perfil no hay ninguna categoría posible: son un
            // nivel cruzado con un sexo.
            // Por NIVEL, sin repetir: la membresía guarda el nivel y nada
            // más, así que un club con «B singles» y «B dobles» ofrecía dos
            // veces «B» en este mismo desplegable.
            const disponibles =
              player.sex === null ? [] : clubLevels(community.categories, player.sex);
            const elegida = levels[player.userId] ?? disponibles[0];

            return (
              <tr key={player.userId}>
                <PersonCell
                  userId={player.userId}
                  displayName={player.displayName}
                  avatarPath={player.avatarPath}
                  note={
                    player.locationLabel === ''
                      ? player.fullName
                      : `${player.fullName} · ${player.locationLabel}`
                  }
                />

                <td className="actions">
                {player.alreadyMember || agregados.has(player.userId) ? (
                  <span className="tag">{t('members.already')}</span>
                ) : player.sex === null ? (
                  // La persona no eligió categoría en su perfil. Es distinto
                  // de «el club no la corre» y hay que decirlo distinto: acá
                  // lo que falta lo tiene que completar ella.
                  <span className="tag">{t('members.incompleteProfile')}</span>
                ) : disponibles.length === 0 ? (
                  // El club no corre ninguna categoría del sexo de esa
                  // persona. Decirlo es mejor que ofrecer un select vacío.
                  <span className="tag">{t('members.noCategoryForSex')}</span>
                ) : (
                  <>
                    <Select<CategoryLevel>
                      id={`add-level-${player.userId}`}
                      aria-label={t('members.categoryOf', { name: player.displayName })}
                      // `disponibles` no está vacío: lo comprueba la rama de
                      // arriba, así que el primero siempre existe.
                      value={elegida ?? disponibles[0]!}
                      options={disponibles.map((nivel) => ({
                        value: nivel,
                        label: t(`categoryLevel.${nivel}`),
                      }))}
                      onChange={(level) =>
                        setLevels((current) => ({ ...current, [player.userId]: level }))
                      }
                    />
                    <button
                      type="button"
                      disabled={elegida === undefined}
                      onClick={() => {
                        if (elegida !== undefined) void add(player, elegida);
                      }}
                    >
                      {t('members.add')}
                    </button>
                  </>
                )}
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Reglamento
// =====================================================================

/**
 * El reglamento del club, que aplica a TODOS sus torneos.
 *
 * Vive acá y no en cada torneo porque es el mismo siempre. Copiarlo torneo
 * por torneo garantiza que tarde o temprano dos difieran sin que nadie lo
 * haya decidido, y entonces nadie sabe cuál manda.
 *
 * Se muestra a todo el mundo —quien se va a inscribir tiene derecho a leerlo
 * antes— y lo edita solo quien administra.
 */
/**
 * LA FICHA DEL CLUB, editable: de qué se trata, dónde se juega y el grupo.
 *
 * Se escribían solo al crear el club, y uno que cambió de cancha —o que abrió
 * un grupo nuevo— se quedaba con la ficha del primer día. Es lo primero que
 * lee alguien que está decidiendo si pedir entrar, así que estar
 * desactualizada cuesta gente.
 *
 * EL NOMBRE NO SE EDITA ACÁ y no es un olvido: cambiarlo cambia la dirección
 * del club, que ya anda circulando por WhatsApp.
 */
function DetailsSection({
  community,
  canManage,
  onChanged,
}: {
  community: Community;
  canManage: boolean;
  onChanged: (community: Community) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(community.description);
  const [locationText, setLocationText] = useState(community.locationText);
  const [whatsappGroupUrl, setWhatsappGroupUrl] = useState(community.whatsappGroupUrl ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Para quien solo mira, la ficha ya se ve arriba: acá no hay nada que
  // agregar salvo el botón de editarla.
  if (!canManage) return null;

  function abrir(): void {
    setDescription(community.description);
    setLocationText(community.locationText);
    setWhatsappGroupUrl(community.whatsappGroupUrl ?? '');
    setError(null);
    setEditing(true);
  }

  async function save(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(
        await api.setCommunityDetails(community.id, {
          description: description.trim(),
          locationText: locationText.trim(),
          whatsappGroupUrl: whatsappGroupUrl.trim(),
        }),
      );
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('communities.details')}</h2>
        {!editing && (
          <button type="button" className="link" onClick={abrir}>
            {t('common.edit')}
          </button>
        )}
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {editing && (
        <>
          {/* EL LOGO, PRIMERO. Es lo que más se ve de un club —en su ficha, en
              la lista y en la vitrina de torneos— y hasta hace poco solo se
              podía poner al crearlo. Va acá y no suelto en la pantalla porque
              cambiarlo ES editar el club.

              Se guarda con SU PROPIO botón y no con el de abajo: una imagen
              viaja como `multipart` y el resto de la ficha como JSON, así que
              son dos peticiones distintas. Atarlas obligaría a subir la foto
              de nuevo cada vez que se corrige una coma de la descripción. */}
          <LogoBox community={community} onChanged={onChanged} />

          <div className="field">
            <label htmlFor="community-description">{t('newCommunity.description')}</label>
            <textarea
              id="community-description"
              rows={5}
              value={description}
              disabled={sending}
              maxLength={COMMUNITY_LIMITS.descriptionMax}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="help">{t('newCommunity.descriptionHelp')}</p>
          </div>

          <div className="field">
            <label htmlFor="community-location">{t('newCommunity.location')}</label>
            <input
              id="community-location"
              type="text"
              value={locationText}
              disabled={sending}
              maxLength={COMMUNITY_LIMITS.locationMax}
              onChange={(e) => setLocationText(e.target.value)}
            />
            <p className="help">{t('newCommunity.locationHelp')}</p>
          </div>

          <div className="field">
            <label htmlFor="community-whatsapp">{t('newCommunity.whatsapp')}</label>
            <input
              id="community-whatsapp"
              type="url"
              value={whatsappGroupUrl}
              disabled={sending}
              maxLength={COMMUNITY_LIMITS.whatsappUrlMax}
              onChange={(e) => setWhatsappGroupUrl(e.target.value)}
            />
            <p className="help">{t('newCommunity.whatsappHelp')}</p>
          </div>

          <div className="button-row">
            <button type="button" onClick={() => void save()} disabled={sending}>
              {sending ? t('common.sending') : t('common.save')}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => setEditing(false)}
              disabled={sending}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function RulesSection({
  community,
  canManage,
  onChanged,
}: {
  community: Community;
  canManage: boolean;
  onChanged: (community: Community) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(community.tournamentRules ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rules = community.tournamentRules;
  const vacio = rules === null || rules.trim() === '';

  // Sin reglamento y sin poder escribirlo, la sección sería un título con un
  // «todavía no hay» debajo: ruido para quien solo mira.
  if (vacio && !canManage) return null;

  async function save(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(await api.setCommunityRules(community.id, draft.trim()));
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('rules.title')}</h2>
        {canManage && !editing && (
          <button
            type="button"
            className="link"
            // EL NOMBRE DICE QUÉ EDITA. Estas pantallas tienen dos y tres
            // botones «Editar» —la ficha, el logo, el reglamento— y todos se
            // llamaban igual: con el título al lado se entiende mirando, pero
            // quien usa un lector de pantalla oye «Editar, Editar, Editar».
            // Solo cuando dice «Editar»: con «Escribir el reglamento» el texto
            // visible ya es específico, y un nombre accesible distinto del que
            // se lee en pantalla confunde a quien usa las dos cosas.
            aria-label={vacio ? undefined : t('rules.edit')}
            onClick={() => {
              setDraft(community.tournamentRules ?? '');
              setError(null);
              setEditing(true);
            }}
          >
            {vacio ? t('rules.write') : t('common.edit')}
          </button>
        )}
      </div>

      <p className="help">{t('rules.help')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {editing ? (
        <>
          <div className="field">
            <label htmlFor="community-rules">{t('rules.label')}</label>
            <textarea
              id="community-rules"
              rows={12}
              value={draft}
              disabled={sending}
              maxLength={TOURNAMENT_LIMITS.rulesMax}
              placeholder={t('rules.placeholder')}
              onChange={(e) => setDraft(e.target.value)}
            />
            {/*
              LA CUENTA REGRESIVA, SOLO CUANDO FALTA POCO.
              Decía «Quedan {{n}} caracteres» con las llaves a la vista: el
              texto espera `n` y se le pasaba `count`, así que i18next no
              reemplazaba nada. Y ahora que el tope son doscientos mil, un
              contador permanente es ruido que nadie va a leer nunca — se
              muestra en los últimos quinientos, que es cuando sirve de algo.
            */}
            {TOURNAMENT_LIMITS.rulesMax - draft.length <= 500 && (
              <p className="help">
                {t('rules.remaining', { n: TOURNAMENT_LIMITS.rulesMax - draft.length })}
              </p>
            )}
          </div>
          <div className="button-row">
            <button type="button" onClick={() => void save()} disabled={sending}>
              {sending ? t('common.sending') : t('common.save')}
            </button>
            <button type="button" className="link" onClick={() => setEditing(false)} disabled={sending}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : vacio ? (
        <p className="help">{t('rules.empty')}</p>
      ) : (
        // `white-space: pre-wrap` en el CSS: el reglamento viene escrito con
        // saltos de línea y numeración a mano, y respetarlos es la diferencia
        // entre un reglamento y un párrafo ilegible.
        <p className="rules-text">{rules}</p>
      )}
    </section>
  );
}
